import {
  Button,
  Field,
  makeStyles,
  Text,
  tokens,
} from "@fluentui/react-components";
import React, { FormEvent, useMemo, useState } from "react";
import { ValidationErrors } from "../../../types/common";
import {
  BaseCustomerProfileParams,
  BaseCustomerProfileParamsKeys,
  CustomerProfileTypeOfBusinessEnum,
} from "../../../types/drayage";
import CurrencyField from "../../common/CurrencyField/CurrencyField";
import DropdownField from "../../common/DropdownField/DropdownField";
import InputField from "../../common/InputField/InputField";
import PhoneField from "../../common/PhoneField/PhoneField";
import TextareaField from "../../common/TextareaField/TextareaField";
import { CustomerProfileTypeOfBusinessOptions } from "./CustomerForm.data";
import { createCustomerFormValidation } from "./CustomerForm.validator";
import { validateField, validateForm } from "../Form.validators";
import { FieldValidationConfig, ValidatableValue } from "../Form.types";

/* global HTMLFormElement */

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  title: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
  },
  fieldRow: {
    display: "flex",
    gap: "8px",
    width: "100%",
  },
  field: {
    flex: 1,
  },
  fullWidth: {
    width: "100%",
  },
  buttonsContainer: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    marginTop: "16px",
  },
});

type FormRecord = Record<keyof BaseCustomerProfileParams, ValidatableValue>;

interface CustomerFormProps {
  onSubmit: (data: BaseCustomerProfileParams) => void;
  onCancel: () => void;
  error?: string;
  saving?: boolean;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  onSubmit,
  onCancel,
  error,
  saving,
}) => {
  const styles = useStyles();

  const [formData, setFormData] = useState<BaseCustomerProfileParams>({
    name: "",
    info: "",
    typeOfBusiness: CustomerProfileTypeOfBusinessEnum.Broker,
    mcNumber: "",
    email: "",
    phoneNumber: "",
    streetAddress: "",
    city: "",
    state: "",
    zip: "",
    creditLimit: 0,
    creditScore: "",
    scacNumber: "",
    billingEmail: "",
    trackingEmail: "",
    notes: "",
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );

  const validationConfig = useMemo(
    () => createCustomerFormValidation(formData.typeOfBusiness),
    [formData.typeOfBusiness],
  );

  const handleChange = (
    field: BaseCustomerProfileParamsKeys,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    const fieldConfig = validationConfig[field];
    if (fieldConfig) {
      const result = validateField<string>(
        value,
        fieldConfig as FieldValidationConfig<string>,
      );
      if (!result.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          [field]: result.error,
        }));
      } else {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = validateForm<FormRecord>(
      formData as FormRecord,
      validationConfig,
    );
    setValidationErrors(errors);

    if (Object.keys(errors).length === 0 && !saving) {
      onSubmit(formData);
    }
  };

  const isSaveDisabled = Object.keys(validationErrors).length > 0;

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <Text className={styles.title}>
        New Customer Profile: {formData.name || "Untitled"}
      </Text>

      <Field
        id="name"
        className={styles.fullWidth}
        label="Name"
        validationState={validationErrors.name ? "error" : "none"}
        validationMessage={validationErrors.name}
      >
        <InputField
          value={formData.name}
          onChange={(value) => handleChange("name", value)}
          placeholder="Enter name"
          required
        />
      </Field>

      <Field
        id="typeOfBusiness"
        className={styles.fullWidth}
        label="Type of Business"
        validationState={validationErrors.typeOfBusiness ? "error" : "none"}
        validationMessage={validationErrors.typeOfBusiness}
      >
        <DropdownField
          options={CustomerProfileTypeOfBusinessOptions}
          selectedValue={formData.typeOfBusiness}
          onSelect={(value) => handleChange("typeOfBusiness", value)}
          placeholder="Select type of business"
        />
      </Field>

      <Field
        id={
          formData.typeOfBusiness ===
          CustomerProfileTypeOfBusinessEnum.FreightForwarder
            ? "ffNumber"
            : "mcNumber"
        }
        className={styles.fullWidth}
        label={
          formData.typeOfBusiness ===
          CustomerProfileTypeOfBusinessEnum.FreightForwarder
            ? "FF Number"
            : "MC Number"
        }
        validationState={validationErrors.mcNumber ? "error" : "none"}
        validationMessage={validationErrors.mcNumber}
      >
        <InputField
          value={formData.mcNumber}
          onChange={(value) => handleChange("mcNumber", value)}
        />
      </Field>

      <Field
        id="email"
        className={styles.fullWidth}
        label="Email"
        validationState={validationErrors.email ? "error" : "none"}
        validationMessage={validationErrors.email}
      >
        <InputField
          value={formData.email}
          onChange={(value) => handleChange("email", value)}
          required
        />
      </Field>

      <Field
        id="phoneNumber"
        className={styles.fullWidth}
        label="Phone Number"
        validationState={validationErrors.phoneNumber ? "error" : "none"}
        validationMessage={validationErrors.phoneNumber}
      >
        <PhoneField
          value={formData.phoneNumber}
          onChange={(value) => handleChange("phoneNumber", value)}
          required
        />
      </Field>

      <Field
        id="streetAddress"
        className={styles.fullWidth}
        label="Street Address"
        validationState={validationErrors.streetAddress ? "error" : "none"}
        validationMessage={validationErrors.streetAddress}
      >
        <InputField
          value={formData.streetAddress}
          onChange={(value) => handleChange("streetAddress", value)}
        />
      </Field>

      <Field
        id="city"
        className={styles.fullWidth}
        label="City"
        validationState={validationErrors.city ? "error" : "none"}
        validationMessage={validationErrors.city}
      >
        <InputField
          value={formData.city}
          onChange={(value) => handleChange("city", value)}
        />
      </Field>

      <Field
        id="state"
        className={styles.fullWidth}
        label="State"
        validationState={validationErrors.state ? "error" : "none"}
        validationMessage={validationErrors.state}
      >
        <InputField
          value={formData.state}
          onChange={(value) => handleChange("state", value)}
        />
      </Field>

      <Field
        id="zip"
        className={styles.fullWidth}
        label="ZIP"
        validationState={validationErrors.zip ? "error" : "none"}
        validationMessage={validationErrors.zip}
      >
        <InputField
          value={formData.zip}
          onChange={(value) => handleChange("zip", value)}
        />
      </Field>

      <Field
        id="creditLimit"
        className={styles.fullWidth}
        label="Credit Limit"
        validationState={validationErrors.creditLimit ? "error" : "none"}
        validationMessage={validationErrors.creditLimit}
      >
        <CurrencyField
          value={formData.creditLimit}
          onChange={(value) => handleChange("creditLimit", value)}
        />
      </Field>

      <Field
        id="creditScore"
        className={styles.fullWidth}
        label="Credit Score"
        validationState={validationErrors.creditScore ? "error" : "none"}
        validationMessage={validationErrors.creditScore}
      >
        <InputField
          value={formData.creditScore}
          onChange={(value) => handleChange("creditScore", value)}
        />
      </Field>

      <Field
        id="scacNumber"
        className={styles.fullWidth}
        label="SCAC Number"
        validationState={validationErrors.scacNumber ? "error" : "none"}
        validationMessage={validationErrors.scacNumber}
      >
        <InputField
          value={formData.scacNumber}
          onChange={(value) => handleChange("scacNumber", value)}
        />
      </Field>

      <Field
        id="billingEmail"
        className={styles.fullWidth}
        label="Billing Email"
        validationState={validationErrors.billingEmail ? "error" : "none"}
        validationMessage={validationErrors.billingEmail}
      >
        <InputField
          value={formData.billingEmail}
          onChange={(value) => handleChange("billingEmail", value)}
        />
      </Field>

      <Field
        id="trackingEmail"
        className={styles.fullWidth}
        label="Tracking Email"
        validationState={validationErrors.trackingEmail ? "error" : "none"}
        validationMessage={validationErrors.trackingEmail}
      >
        <InputField
          value={formData.trackingEmail}
          onChange={(value) => handleChange("trackingEmail", value)}
        />
      </Field>

      <Field
        id="notes"
        className={styles.fullWidth}
        label="Notes"
        validationState={validationErrors.notes ? "error" : "none"}
        validationMessage={validationErrors.notes}
      >
        <TextareaField
          value={formData.notes}
          onChange={(value) => handleChange("notes", value)}
          rows={3}
        />
      </Field>

      {error && <Field validationState="error" validationMessage={error} />}

      <div id="actionButtons" className={styles.buttonsContainer}>
        <Button
          id="cancelButton"
          appearance="secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          id="saveButton"
          appearance="primary"
          type="submit"
          disabled={saving || isSaveDisabled}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </form>
  );
};

export default CustomerForm;
